import { FC } from 'react'
import {
  aggressSubmitErrorText,
  getTotalSecondsBeforeExpiration
} from '../../../helpers/orders'
import { getAllUserOrders } from '../../../store/order/selectors'
import { Order, OrderStatus } from '../../../store/order/types'
import { useAppSelector } from '../../../store/types'
import { getServerTimeDelayMs } from '../../../store/ws/selectors'
import DurationTimer from '../../Timer/DurationTimer'
import ModalActionsButtons from '../ModalActionsButtons/ModalActionsButtons'
import ModalFooter from '../ModalFooter/ModalFooter'

export interface Props {
  order: Order
  orderStatusComp: OrderStatus | 'passiveOrderCancelled' | undefined
  type: string
  tradeAmount: number | undefined
  availableAmount: number | undefined
  actionCloseModal: (e: React.MouseEvent<HTMLButtonElement>) => void
  handleCancelAttempt: (e: React.MouseEvent<HTMLButtonElement>) => void
  isCancelAttempt: boolean
  isAmountInputNOK: boolean
  isSubmitting: boolean
  liquidityRemoved?: boolean
  error?: string
  maxTradeSize: number | null
  maxTradeSizeIsHardLimit: boolean
  benchmarkName: string | undefined
  spotCrossSelection: string
}

const AggressorModalFooter: FC<Props> = (props) => {
  const {
    order,
    orderStatusComp,
    type,
    tradeAmount,
    availableAmount,
    actionCloseModal,
    handleCancelAttempt,
    isAmountInputNOK,
    isSubmitting,
    liquidityRemoved,
    error,
    maxTradeSize,
    maxTradeSizeIsHardLimit,
    benchmarkName,
    spotCrossSelection
  } = props
  const allOrders = useAppSelector(getAllUserOrders)
  const serverTimeDelayMs = useAppSelector(getServerTimeDelayMs)

  const getNotAvailableFooter = () => {
    const orderStatus = liquidityRemoved ? 'notAvailable' : orderStatusComp
    return (
      <ModalFooter
        title="NOT AVAILABLE"
        dataTestId="aggressor-status"
        content="Liquidity is no longer available"
        icon={<div />}
        buttonComponent={
          <ModalActionsButtons
            orderStatusComp={
              orderStatus === 'passiveOrderCancelled' ? undefined : orderStatus
            }
            onModalClosed={actionCloseModal}
          />
        }
        customStyles={{
          content: 'bottomNotAvailable',
          icon: 'icnNotAvailable'
        }}
      />
    )
  }

  if (
    liquidityRemoved &&
    orderStatusComp !== 'rejected' &&
    orderStatusComp !== 'cancelled' &&
    orderStatusComp !== 'accepted' &&
    orderStatusComp !== 'error' &&
    order.transactionId &&
    order.transactionId < 1
    // the aggress order will have a 'fake' transactionId before it is submitted
    // only show Liquidity no longer available if the passive order is canceled before the aggress order is submitted
  ) {
    return getNotAvailableFooter()
  }

  const getContent = () => {
    const typeLabel = type === 'buy' ? 'bought' : 'sold'
    const tsyTypelabel = type === 'buy' ? 'sold' : 'bought'

    const getTsyStatus = () => {
      const matchingOrder = Object.values(allOrders).find(
        (value: Order) => value.corpOrderId === order.id
      )
      switch (matchingOrder?.status) {
        case 'accepted':
          return (
            <div>
              Tsy hedge: {tsyTypelabel} {benchmarkName} {matchingOrder.size} @{' '}
              {matchingOrder.displayPrice}
            </div>
          )
        case 'waitingForConfirmation':
          return <div>Waiting on Tsy Hedge Execution...</div>
        case 'rejected':
          return <div>No Tsy Hedge Executed</div>
        default:
          return
      }
    }
    return (
      <div>
        <div>
          You {typeLabel} {tradeAmount} of {availableAmount}
        </div>
        {spotCrossSelection === 'H' && getTsyStatus()}
      </div>
    )
  }

  switch (orderStatusComp) {
    case 'accepted':
      return (
        <ModalFooter
          title={type === 'buy' ? 'BOUGHT' : 'SOLD'}
          dataTestId="aggressor-status"
          content={getContent()}
          icon={null}
          buttonComponent={
            <ModalActionsButtons
              orderStatusComp={orderStatusComp}
              onModalClosed={actionCloseModal}
            />
          }
          customStyles={{
            content: 'bottomContentSuccess',
            icon: 'icnAccepted'
          }}
        />
      )
    case 'pending': // FIXME for now, same behavior as 'waitingForConfirmation'
    case 'waitingForConfirmation':
      const { totalSeconds } = getTotalSecondsBeforeExpiration(
        order.expiration,
        order.submitTime,
        serverTimeDelayMs
      )
      return (
        <ModalFooter
          title="Pending"
          dataTestId="aggressor-status"
          content="Awaiting Counterparty"
          timer={
            order.expiration && (
              <DurationTimer
                duration={totalSeconds}
                expireTime={order.expiration}
              />
            )
          }
          buttonComponent={
            <ModalActionsButtons
              orderStatusComp={orderStatusComp}
              onButtonClicked={handleCancelAttempt}
              onModalClosed={actionCloseModal}
            />
          }
          customStyles={{
            content: 'bottomContentPending',
            icon: 'icnPending'
          }}
        />
      )
    case 'cancelled':
      return (
        <ModalFooter
          title="Cancelled"
          dataTestId="aggressor-status"
          content="Your attempt has been cancelled"
          icon={null}
          buttonComponent={
            <ModalActionsButtons
              orderStatusComp={orderStatusComp}
              onModalClosed={actionCloseModal}
              disabled={isAmountInputNOK || isSubmitting}
            />
          }
          customStyles={{
            content: 'bottomContentRejected',
            icon: 'icnRejected'
          }}
        />
      )
    case 'error':
      return (
        <ModalFooter
          title="Error"
          dataTestId="aggressor-status"
          content={order.statusReason || aggressSubmitErrorText}
          icon={null}
          buttonComponent={
            <ModalActionsButtons
              orderStatusComp={orderStatusComp}
              onModalClosed={actionCloseModal}
              disabled={isAmountInputNOK || isSubmitting}
            />
          }
          customStyles={{
            content: 'bottomContentRejected',
            icon: 'icnRejected'
          }}
        />
      )
    case 'rejected':
      return (
        <ModalFooter
          title="Rejected"
          dataTestId="aggressor-status"
          content="Your order has been rejected"
          icon={<div />}
          buttonComponent={
            <ModalActionsButtons
              orderStatusComp={orderStatusComp}
              onModalClosed={actionCloseModal}
            />
          }
          customStyles={{
            content: 'bottomContentRejected',
            icon: 'icnRejected'
          }}
        />
      )
    default:
      if (error) {
        return (
          <ModalFooter
            title="Error"
            dataTestId="aggressor-status"
            content={error}
            icon={<div />}
            buttonComponent={
              <ModalActionsButtons
                orderStatusComp={
                  orderStatusComp === 'passiveOrderCancelled'
                    ? undefined
                    : orderStatusComp
                }
                onModalClosed={actionCloseModal}
                type="Resubmit"
                disabled={isAmountInputNOK || isSubmitting}
              />
            }
            customStyles={{
              content: 'bottomContentRejected',
              icon: 'icnRejected'
            }}
          />
        )
      } else if (maxTradeSize && tradeAmount && maxTradeSize < tradeAmount) {
        if (availableAmount && availableAmount < tradeAmount) {
          return (
            <ModalFooter
              dataTestId="aggressor-close"
              buttonComponent={
                <ModalActionsButtons
                  onModalClosed={actionCloseModal}
                  type={type}
                  disabled={
                    isAmountInputNOK ||
                    isSubmitting ||
                    orderStatusComp === 'passiveOrderCancelled'
                  }
                />
              }
            />
          )
        }
        if (maxTradeSizeIsHardLimit) {
          return (
            <ModalFooter
              dataTestId="aggressor-close"
              content={
                'The amount of ' +
                tradeAmount +
                ' exceeds your Maximum amount of ' +
                maxTradeSize
              }
              buttonComponent={
                <ModalActionsButtons
                  onModalClosed={actionCloseModal}
                  disabled={true}
                />
              }
              customStyles={{
                content: 'bottomContentRejected',
                icon: 'icnRejected'
              }}
            />
          )
        } else {
          return (
            <ModalFooter
              dataTestId="aggressor-close"
              content={`The amount of ${tradeAmount} exceeds your Maximum amount of ${maxTradeSize}. Are you sure you want to submit a ${
                type === 'buy' ? 'lift' : 'hit'
              } for a size of ${tradeAmount}?`}
              buttonComponent={
                <ModalActionsButtons
                  onModalClosed={actionCloseModal}
                  type={type}
                  disabled={isAmountInputNOK || isSubmitting}
                />
              }
              customStyles={{
                content: 'bottomContentPending',
                icon: 'icnRejected'
              }}
            />
          )
        }
      } else {
        return (
          <ModalFooter
            dataTestId="aggressor-close"
            buttonComponent={
              <ModalActionsButtons
                onModalClosed={actionCloseModal}
                type={type}
                disabled={
                  isAmountInputNOK ||
                  isSubmitting ||
                  orderStatusComp === 'passiveOrderCancelled'
                }
              />
            }
          />
        )
      }
  }
}

export default AggressorModalFooter
